import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { Input } from 'antd';
import dayjs from 'dayjs';
import { FiXCircle, FiCheckCircle, FiSave } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { toastMessage } from '../../utils/toast';
import { AlertBox, preventEnterSubmit } from './common';
import { CenterSelect, DateRangeField, TimeRangeField, SwitchField } from './fields';
import {
    DATE_OVERRIDE_TYPE, toTime, toInputValue, toCenterId, validateDateOverride, getErrorMessage,
    fieldFromMessage, hasErrors
} from './helpers';

const TYPE_OPTIONS = [
    {
        value: DATE_OVERRIDE_TYPE.CLOSED,
        className: 'is-closed',
        icon: <FiXCircle />,
        title: 'Holiday',
        desc: 'Close the center for the day, or only for some hours.'
    },
    {
        value: DATE_OVERRIDE_TYPE.OPEN,
        className: 'is-open',
        icon: <FiCheckCircle />,
        title: 'Special opening',
        desc: 'Open on a weekly off day or holiday, all day or for some hours.'
    }
];

const emptyForm = {
    type: DATE_OVERRIDE_TYPE.CLOSED,
    centerId: '',
    title: '',
    fromDate: '',
    toDate: '',
    wholeDay: true,
    fromTime: '',
    toTime: '',
    isRecurringYearly: false
};

const toForm = (item, defaults) => {
    if (!item) return { ...emptyForm, ...(defaults || {}) };
    return {
        type: item.type,
        centerId: toInputValue(item.centerId),
        title: item.title || '',
        fromDate: item.fromDate || '',
        toDate: item.toDate || '',
        wholeDay: !(item.fromTime || item.toTime),
        fromTime: toTime(item.fromTime),
        toTime: toTime(item.toTime),
        isRecurringYearly: !!item.isRecurringYearly
    };
};

const DateOverrideFormModal = ({ show, item, defaults, centers, onClose, onSaved }) => {
    const [form, setForm] = useState(toForm(item, defaults));
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show) return;
        setForm(toForm(item, defaults));
        setErrors({});
        setServerError('');
    }, [show, item]);

    const setField = (key, value, errorKey = key) => {
        setForm(prev => ({ ...prev, [key]: value }));
        if (errors[errorKey]) setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    };

    const handleDates = (fromDate, toDate) => {
        setForm(prev => ({ ...prev, fromDate, toDate }));
        setErrors(prev => ({ ...prev, fromDate: undefined, toDate: undefined }));
    };

    const handleTimes = (fromTime, toTime) => {
        setForm(prev => ({ ...prev, fromTime, toTime }));
        if (errors.time) setErrors(prev => ({ ...prev, time: undefined }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (saving) return;
        const body = {
            centerId: toCenterId(form.centerId),
            title: form.title.trim(),
            type: form.type,
            fromDate: form.fromDate,
            toDate: form.toDate || form.fromDate,
            fromTime: form.wholeDay ? null : (form.fromTime || null),
            toTime: form.wholeDay ? null : (form.toTime || null),
            isRecurringYearly: form.isRecurringYearly
        };
        const formErrors = validateDateOverride(body, form.wholeDay);
        setErrors(formErrors);
        setServerError('');
        if (hasErrors(formErrors)) return;

        setSaving(true);
        const request = item
            ? slotSettingActions.updateDateOverride(item.id, body)
            : slotSettingActions.createDateOverride(body);
        request.then(res => {
            setSaving(false);
            if (res?.type === DATE_OVERRIDE_TYPE.CLOSED && res.affectedBookings > 0) {
                const count = res.affectedBookings;
                toastMessage('warning', `Saved. ${count} confirmed booking${count > 1 ? 's fall' : ' falls'} on this holiday. They are not cancelled automatically.`);
            } else {
                toastMessage('success', item ? 'Changes saved' : `${form.type === DATE_OVERRIDE_TYPE.CLOSED ? 'Holiday' : 'Special opening'} added`);
            }
            onSaved(res);
        }).catch(err => {
            setSaving(false);
            const message = getErrorMessage(err, 'Unable to save. Please try again.');
            const field = fieldFromMessage(message);
            if (field) setErrors({ [field]: message });
            setServerError(message);
        });
    };

    const isClosed = form.type === DATE_OVERRIDE_TYPE.CLOSED;
    const endDate = form.toDate || form.fromDate;
    const dayCount = form.fromDate && endDate >= form.fromDate ? dayjs(endDate).diff(dayjs(form.fromDate), 'day') + 1 : 0;
    const dateError = errors.fromDate || errors.toDate;

    return (
        <Modal show={show} onHide={saving ? undefined : onClose} centered size="lg" className="ss-modal"
            backdrop={saving ? 'static' : true} enforceFocus={false}>
            <Form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title as="h5">{item ? 'Edit date override' : `Add ${isClosed ? 'holiday' : 'special opening'}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {serverError && <AlertBox title="Not saved" messages={serverError} onClose={() => setServerError('')} />}

                    <div className="ss-section-label">Type</div>
                    <div className="ss-type-options" role="radiogroup" aria-label="Type">
                        {TYPE_OPTIONS.map(option => (
                            <button key={option.value} type="button" role="radio" aria-checked={form.type === option.value}
                                className={`ss-type-option ${option.className} ${form.type === option.value ? 'active' : ''}`}
                                disabled={saving}
                                onClick={() => setField('type', option.value)}>
                                <span className="ss-type-option-icon">{option.icon}</span>
                                <span>
                                    <span className="ss-type-option-title">{option.title}</span>
                                    <span className="ss-type-option-desc">{option.desc}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                    {errors.type && <span className="ss-field-error">{errors.type}</span>}

                    <Row className="g-3 mt-1">
                        <Col md={6}>
                            <Form.Label htmlFor="do-title">Title <span className="text-danger">*</span></Form.Label>
                            <Input id="do-title" value={form.title} maxLength={200} disabled={saving}
                                status={errors.title ? 'error' : undefined}
                                placeholder={isClosed ? 'e.g. UAE National Day' : 'e.g. Special Sunday opening'}
                                onChange={e => setField('title', e.target.value)} />
                            {errors.title && <span className="ss-field-error">{errors.title}</span>}
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="do-center">Center</Form.Label>
                            <CenterSelect id="do-center" value={form.centerId} centers={centers} allLabel="All centers"
                                disabled={saving} error={errors.centerId}
                                onChange={value => setField('centerId', value)} />
                            {errors.centerId
                                ? <span className="ss-field-error">{errors.centerId}</span>
                                : <span className="ss-hint">A center entry beats an all-centers entry on the same date.</span>}
                        </Col>
                        <Col xs={12}>
                            <Form.Label htmlFor="do-dates">Dates <span className="text-danger">*</span></Form.Label>
                            <DateRangeField id="do-dates" from={form.fromDate} to={form.toDate} disabled={saving}
                                error={dateError} placeholder={['From date', 'To date (optional)']}
                                onChange={handleDates} />
                            {dateError
                                ? <span className="ss-field-error">{dateError}</span>
                                : <span className="ss-hint">{dayCount ? `${dayCount} day${dayCount > 1 ? 's' : ''}` : 'Pick one date for a single day, or a range.'}</span>}
                        </Col>

                        <Col md={6}>
                            <div className="ss-switch-tile">
                                <div>
                                    <div className="ss-switch-title">Whole day</div>
                                    <span className="ss-hint mt-0">Switch off to set a time range.</span>
                                </div>
                                <SwitchField id="do-wholeDay" checked={form.wholeDay} disabled={saving}
                                    onChange={checked => setField('wholeDay', checked, 'time')} />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="ss-switch-tile">
                                <div>
                                    <div className="ss-switch-title">Repeat every year</div>
                                    <span className="ss-hint mt-0">For fixed-date holidays. Max 31 days.</span>
                                </div>
                                <SwitchField id="do-recurring" checked={form.isRecurringYearly} disabled={saving}
                                    onChange={checked => setField('isRecurringYearly', checked, 'toDate')} />
                            </div>
                        </Col>

                        {!form.wholeDay && (
                            <Col xs={12}>
                                <Form.Label htmlFor="do-time">{isClosed ? 'Closed from – to' : 'Open from – to'} <span className="text-danger">*</span></Form.Label>
                                <TimeRangeField id="do-time" from={form.fromTime} to={form.toTime} disabled={saving}
                                    error={errors.time} onChange={handleTimes} />
                                {errors.time
                                    ? <span className="ss-field-error">{errors.time}</span>
                                    : <span className="ss-hint">{isClosed ? 'The center is closed only in this time range.' : 'The center is open only in this time range.'}</span>}
                            </Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        <FiSave /> {saving ? 'Saving...' : item ? 'Save changes' : 'Add'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DateOverrideFormModal;
